// src/hooks/useClients.ts
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Client } from '@/types/Offer';

export interface ClientData extends Client {
  id: string;
  created_at: string;
  updated_at: string;
  last_gus_update?: string;
}

export function useClients() {
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setClients(data || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err instanceof Error ? err.message : 'Błąd pobierania klientów');
    } finally {
      setLoading(false);
    }
  };

  const getClient = async (nip: string): Promise<ClientData | null> => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('nip', nip)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Nie znaleziono
        throw error;
      }
      return data;
    } catch (err) {
      console.error('Error fetching client:', err);
      return null;
    }
  };

  const createClient = async (clientData: Client): Promise<ClientData | null> => {
    try {
      // Najpierw sprawdź czy klient z tym NIP już istnieje
      const existing = await getClient(clientData.nip);
      if (existing) {
        // Jeśli istnieje, zaktualizuj go zamiast tworzyć nowy
        return await updateClient(existing.id, clientData);
      }

      const { data, error } = await supabase
        .from('clients')
        .insert([{
          ...clientData,
          wojewodztwo: clientData.wojewodztwo || '',
          powiat: clientData.powiat || '',
          gmina: clientData.gmina || ''
        }])
        .select()
        .single();

      if (error) {
        // Jeśli błąd to duplikat klucza, spróbuj pobrać istniejącego klienta
        if (error.code === '23505' && error.message.includes('nip')) {
          const existing = await getClient(clientData.nip);
          if (existing) {
            return await updateClient(existing.id, clientData);
          }
        }
        throw error;
      }
      
      await fetchClients(); // Odśwież listę
      return data;
    } catch (err) {
      console.error('Error creating client:', err);
      setError(err instanceof Error ? err.message : 'Błąd tworzenia klienta');
      return null;
    }
  };

  const updateClient = async (clientId: string, updates: Partial<Client>): Promise<ClientData | null> => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', clientId)
        .select()
        .single();

      if (error) throw error;
      
      await fetchClients(); // Odśwież listę
      return data;
    } catch (err) {
      console.error('Error updating client:', err);
      setError(err instanceof Error ? err.message : 'Błąd aktualizacji klienta');
      return null;
    }
  };

  const searchClients = async (query: string): Promise<ClientData[]> => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .or(`name.ilike.%${query}%,nip.ilike.%${query}%,email.ilike.%${query}%`)
        .order('name', { ascending: true })
        .limit(10);

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error searching clients:', err);
      return [];
    }
  };

  return {
    clients,
    loading,
    error,
    getClient,
    createClient,
    updateClient,
    searchClients,
    refreshClients: fetchClients
  };
}
